import { SelectionModel } from '@angular/cdk/collections';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Permission } from 'src/app/common/enums';
import { FilterOperators } from 'src/app/common/enums/filter-operators';
import { PropertyFilterOperator } from 'src/app/common/interfaces/property-filter-operator';
import { buildFilterArray } from 'src/app/common/utils/build-filter-array';
import { DictionaryService } from 'src/app/data/dictionary.service';
import { Filter } from 'src/app/models/common/filter';
import { DictionaryItem } from 'src/app/models/DictionaryItem';
import { EmployerObjectDictionaryDto } from 'src/app/models/dtos/employer-object-dictionary-dto';
import { AbsenceStatus } from 'src/app/models/enums/absence-status';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { AbsenceService } from '../../data/absence.service';
import { SubordinatesFlatNode, SubordinatesNode } from '../../models/dtos/subordinates-node-dto';
import { DisableErrorStateMatcher } from 'src/app/shared/error-state-matchers/disable-error-state-matcher';

@Component({
  selector: 'app-absence-list-filters',
  templateUrl: './absence-list-filters.component.html',
  styleUrls: ['./absence-list-filters.component.scss'],
  standalone: false
})
export class AbsenceListFiltersComponent implements OnInit, OnDestroy {
  private absenceService = inject(AbsenceService);
  private dictionaryService = inject(DictionaryService);

  public static readonly operatorsMap: Map<string, PropertyFilterOperator> = new Map<string, PropertyFilterOperator>([
    ['firstName', { property: 'FirstName', operator: FilterOperators.Contains }],
    ['lastName', { property: 'LastName', operator: FilterOperators.Contains }],
    ['companyId', { property: 'CompanyId', operator: FilterOperators.Equal }],
    ['employerId', { property: 'EmployerId', operator: FilterOperators.Equal }],
    ['employerObjectId', { property: 'EmployerObjectId', operator: FilterOperators.Equal }],
    ['absenceTypeId', { property: 'AbsenceTypeId', operator: FilterOperators.Equal }],
    ['statusId', { property: 'StatusId', operator: FilterOperators.Equal }],
    ['absenceSyncStatusId', { property: 'AbsenceSyncStatusId', operator: FilterOperators.Equal }],
    ['startDate', { property: 'StartDate', operator: FilterOperators.DateGreaterThanOrEqual }],
    ['endDate', { property: 'EndDate', operator: FilterOperators.DateLessThanOrEqual }],
    ['subordinates', { property: 'Subordinates', operator: FilterOperators.Equal }],
  ]);


  constructor() {
    this.treeFlattener = new MatTreeFlattener(this.transformer, this.getLevel, this.isExpandable, this.getChildren);
    this.treeControl = new FlatTreeControl<SubordinatesFlatNode>(this.getLevel, this.isExpandable);
  }

  @Input() formGroup: UntypedFormGroup;
  public readonly isSentToExteralSystemPermissions: Permission[] = [Permission.ShowIsSentToExteralSystemFilter];
  public readonly matcher = new DisableErrorStateMatcher();

  @Output() filterGridData = new EventEmitter<Filter[]>();

  companies$: Observable<DictionaryItem[]>;
  employerObjects$: Observable<EmployerObjectDictionaryDto[]>;
  employers$: Observable<DictionaryItem[]>;
  absenceTypes$: Observable<DictionaryItem[]>;
  absenceStatuses$: Observable<DictionaryItem[]>;
  absenceSyncStatuses$: Observable<DictionaryItem[]>;

  flatNodeMap = new Map<SubordinatesFlatNode, SubordinatesNode>();
  nestedNodeMap = new Map<SubordinatesNode, SubordinatesFlatNode>();
  selectedParent: SubordinatesFlatNode | null = null;
  treeControl: FlatTreeControl<SubordinatesFlatNode>;
  treeFlattener: MatTreeFlattener<SubordinatesNode, SubordinatesFlatNode>;
  dataSource: Promise<MatTreeFlatDataSource<SubordinatesNode, SubordinatesFlatNode>>;
  checklistSelection: SelectionModel<SubordinatesFlatNode>;

  get company(): UntypedFormControl {
    return this.formGroup.get('company') as UntypedFormControl;
  }

  get employer(): UntypedFormControl {
    return this.formGroup.get('employer') as UntypedFormControl;
  }

  get employerObject(): UntypedFormControl {
    return this.formGroup.get('employerObject') as UntypedFormControl;
  }

  get employerObjectId(): UntypedFormControl {
    return this.formGroup.get('employerObjectId') as UntypedFormControl;
  }

  get companyId(): UntypedFormControl {
    return this.formGroup.get('companyId') as UntypedFormControl;
  }

  get employerId(): UntypedFormControl {
    return this.formGroup.get('employerId') as UntypedFormControl;
  }

  private readonly unsubscribe$ = new Subject<void>();
  private readonly autocompleteDebounceTimeMs = 500;
  private readonly formChangeDebounceTimeMs = 1000;

  ngOnInit(): void {
    this.onCompanyChange();
    this.onEmployerChange();
    this.onEmployerObjectChange();
    this.absenceService.getSupervaiorsFilterOptions().subscribe((data) => {
      var dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
      dataSource.data = data;
      this.dataSource = Promise.resolve(dataSource);

      this.setInitialSelection();
    });

    this.formGroup.valueChanges.pipe(takeUntil(this.unsubscribe$), debounceTime(this.formChangeDebounceTimeMs)).subscribe(() => {
      if (this.formGroup.invalid) return;
      this.filterGridData.emit(buildFilterArray(this.formGroup, AbsenceListFiltersComponent.operatorsMap));
    });

    this.absenceTypes$ = this.dictionaryService.getAbsenceTypes();
    this.absenceStatuses$ = this.dictionaryService
      .getAbsenceStatuses()
      .pipe(map((absenceStatuses) => absenceStatuses.filter((absenceStatus) => absenceStatus.Id !== AbsenceStatus.Draft)));
    this.absenceSyncStatuses$ = this.dictionaryService.getAbsenceSyncStatuses();

    if (!this.checklistSelection) {
      this.checklistSelection = new SelectionModel<SubordinatesFlatNode>(true);
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  displayValue = (value: DictionaryItem): string | undefined => value?.Name;

  private onEmployerObjectChange() {
    this.employerObjects$ = this.employerObject.valueChanges.pipe(
      takeUntil(this.unsubscribe$),
      debounceTime(this.autocompleteDebounceTimeMs),
      tap((value: any) => {
        if (value?.Id) {
          this.formGroup.patchValue({ employerObjectId: value?.Id });
        } else if (!value) {
          this.employerObject.reset(undefined, { emitEvent: false });
          this.employerObjectId.reset();
        }
      }),
      switchMap((value: string) => (value ? this.dictionaryService.getEmployerObjects(value) : of([]))),
    );
  }

  private onCompanyChange() {
    this.companies$ = this.company.valueChanges.pipe(
      takeUntil(this.unsubscribe$),
      debounceTime(this.autocompleteDebounceTimeMs),
      tap((value: any) => {
        if (value?.Id) {
          this.formGroup.patchValue({ companyId: value?.Id });
        } else if (!value) {
          this.company.reset(undefined, { emitEvent: false });
          this.companyId.reset();
        }
      }),
      switchMap((value: string) => (value ? this.dictionaryService.getCompanies(value) : of([]))),
    );
  }

  private onEmployerChange() {
    this.employers$ = this.employer.valueChanges.pipe(
      takeUntil(this.unsubscribe$),
      debounceTime(this.autocompleteDebounceTimeMs),
      tap((value: any) => {
        if (value?.Id) {
          this.formGroup.patchValue({ employerId: value?.Id });
        } else if (!value) {
          this.employer.reset(undefined, { emitEvent: false });
          this.employerId.reset();
        }
      }),
      switchMap((value: string) => (value ? this.dictionaryService.getEmployers(value) : of([]))),
    );
  }

  getLevel = (node: SubordinatesFlatNode) => node.level;
  isExpandable = (node: SubordinatesFlatNode) => node.expandable;
  getChildren = (node: SubordinatesNode): SubordinatesNode[] => node.Children;
  hasChild = (_: number, _nodeData: SubordinatesFlatNode) => _nodeData.expandable;
  hasNoContent = (_: number, _nodeData: SubordinatesFlatNode) => _nodeData.id === '';

  transformer = (node: SubordinatesNode, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode = existingNode && existingNode.id === node.Item?.Id ? existingNode : new SubordinatesFlatNode();
    flatNode.name = node.Item.Name;
    flatNode.id = node.Item.Id;
    flatNode.level = level;
    flatNode.expandable = !!node.Children;
    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);

    return flatNode;
  };

  descendantsAllSelected(node: SubordinatesFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected = descendants.every((child) => this.checklistSelection.isSelected(child));
    return descAllSelected;
  }

  descendantsPartiallySelected(node: SubordinatesFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const result = descendants.some((child) => this.checklistSelection.isSelected(child));
    return result && !this.descendantsAllSelected(node);
  }

  subortinatesItemSelectionToggle(node: SubordinatesFlatNode): void {
    this.checklistSelection.toggle(node);
    const descendants = this.treeControl.getDescendants(node);
    this.checklistSelection.isSelected(node) ? this.checklistSelection.select(...descendants) : this.checklistSelection.deselect(...descendants);

    descendants.every((child) => this.checklistSelection.isSelected(child));
    this.checkAllParentsSelection(node);
    this.updateSubordinatesFormControl();
  }

  subortinatesLeafItemSelectionToggle(node: SubordinatesFlatNode): void {
    this.checklistSelection.toggle(node);
    this.checkAllParentsSelection(node);
    this.updateSubordinatesFormControl();
  }

  checkAllParentsSelection(node: SubordinatesFlatNode): void {
    let parent: SubordinatesFlatNode | null = this.getParentNode(node);
    while (parent) {
      this.checkRootNodeSelection(parent);
      parent = this.getParentNode(parent);
    }
  }

  checkRootNodeSelection(node: SubordinatesFlatNode): void {
    const nodeSelected = this.checklistSelection.isSelected(node);
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected = descendants.every((child) => this.checklistSelection.isSelected(child));
    if (nodeSelected && !descAllSelected) {
      this.checklistSelection.deselect(node);
    } else if (!nodeSelected && descAllSelected) {
      this.checklistSelection.select(node);
    }
  }

  getParentNode(node: SubordinatesFlatNode): SubordinatesFlatNode | null {
    const currentLevel = this.getLevel(node);

    if (currentLevel < 1) {
      return null;
    }

    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];

      if (this.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  getSelectedItemsNames(): string {
    if (!this.checklistSelection.selected.length) return '';
    return this.checklistSelection.selected.map((s) => s.name).join(',');
  }

  getSelectedItemsIds(): string[] {
    if (!this.checklistSelection.selected.length) return [];
    return this.checklistSelection.selected.map((s) => s.id);
  }

  public setInitialSelection(): void {
    const subordinateIds = this.formGroup.get('subordinates')?.value as string[];

    const selectedSubordinates = [...this.flatNodeMap.keys()].filter((subordinates) => subordinateIds?.includes(subordinates.id));

    this.checklistSelection.setSelection(...selectedSubordinates);
  }

  private updateSubordinatesFormControl() {
    const selectedItems = this.getSelectedItemsIds();
    this.formGroup.patchValue({ subordinates: selectedItems });
  }
}
